import React, { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { useForm } from "react-hook-form";
import { LineItem, Move, MoveInputs } from "@/types";
import { Form, FormControl, FormField, FormItem } from "@/components/ui/form";
import { useGetMoveQuery, useUpdateMoveMutation } from "@/store/moves/apis";
import LineItems from "../EditMove/LineItems";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Separator } from "@/components/ui/separator";
import { Button } from "@/components/ui/button";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group";
import { useSelector } from "react-redux";
import { checkMobileToken } from "@/store/session/slice";
import { cn } from "@/lib/utils";
import { Loading } from "@/components/Loading";
import { Loader2 } from "lucide-react";

const MoveInvoice: React.FC<{
  move?: Move;
  onSuccess?: () => void;
  onCancel?: () => void;
  buttonsPosition?: "top" | "bottom";
}> = ({ move, onSuccess, onCancel, buttonsPosition = "top" }) => {
  const { id } = useParams<{ id: string }>();
  const {
    data: loadedMove,
    isLoading,
    error,
  } = useGetMoveQuery(id, {
    skip: !!move,
  });
  const [updateMoveApi, { isLoading: isUpdating }] = useUpdateMoveMutation();
  // const [isSubmitting, setIsSubmitting] = useState(false);
  const [lineItemsTotal, setLineItemsTotal] = useState(0);
  const [selectedPaymentMethods, setSelectedPaymentMethods] = useState<
    string[]
  >([]);
  const inApp = useSelector(checkMobileToken);
  const [isAlertOpen, setIsAlertOpen] = useState(false);

  move = move || loadedMove;
  const moveId = move?.id || id;

  const form = useForm<MoveInputs>({
    defaultValues: {
      line_items: [],
      invoice: null,
    },
  });

  const handleSubmitInvoiceOnApp = () => {
    // setIsSubmitting(true);

    handleSubmit({
      line_items: form.getValues("line_items"),
      invoice: form.getValues("invoice"),
    } as MoveInputs)
      .then((response) => {
        console.log("Invoice submitted successfully:", response);

        // @ts-expect-error - window["ReactNativeWebView"] is not defined in the browser
        if (window["ReactNativeWebView"]) {
          // @ts-expect-error - window["ReactNativeWebView"] is not defined in the browser
          window["ReactNativeWebView"].postMessage(
            JSON.stringify({
              type: "invoice_submitted",
              success: true,
            })
          );
        }
      })
      .catch((error: Error) => {
        console.error("Error submitting invoice:", error);

        // Send error back to the mobile app
        // @ts-expect-error - window["ReactNativeWebView"] is not defined in the browser
        if (window["ReactNativeWebView"]) {
          // @ts-expect-error - window["ReactNativeWebView"] is not defined in the browser
          window["ReactNativeWebView"].postMessage(
            JSON.stringify({
              type: "invoice_submitted",
              success: false,
              error: error.message,
            })
          );
        }
      })
      .finally(() => {
        // setIsSubmitting(false);
      });
  };

  useEffect(() => {
    if (inApp) {
      window.addEventListener(
        "submitInvoice",
        handleSubmitInvoiceOnApp as EventListener
      );
    }

    return () => {
      if (inApp) {
        window.removeEventListener(
          "submitInvoice",
          handleSubmitInvoiceOnApp as EventListener
        );
      }
    };
  }, [form, inApp]);

  useEffect(() => {
    if (move) {
      form.reset(move);

      const items = move.line_items || [];
      const initialTotal = items.reduce((acc: number, item: LineItem) => {
        return (
          acc +
          (parseFloat(item.price?.toString() || "0") *
            parseFloat(item.qty?.toString() || "0") || 0)
        );
      }, 0);
      setLineItemsTotal(initialTotal);
    }
  }, [form, move]);

  useEffect(() => {
    const items = form.watch("line_items");
    const initialTotal = items.reduce((acc, item) => {
      return (
        acc +
        (parseFloat(item.price?.toString() || "0") *
          parseFloat(item.qty?.toString() || "0") || 0)
      );
    }, 0);

    setLineItemsTotal(initialTotal);

    const subscription = form.watch((value, { name }) => {
      if (
        name &&
        name.startsWith("line_items.") &&
        (name.endsWith(".price") ||
          name.endsWith(".qty") ||
          name.endsWith(".discount"))
      ) {
        const newTotal =
          value.line_items?.reduce((acc, item) => {
            return (
              acc +
              ((parseFloat(item?.price?.toString() || "0") -
                parseFloat(item?.discount?.toString() || "0")) *
                parseFloat(item?.qty?.toString() || "0") || 0)
            );
          }, 0) || 0;
        setLineItemsTotal(newTotal);
      }
    });

    return () => subscription.unsubscribe();
  }, [form, form.watch, form.setValue]);

  const tipAmount = form.watch("invoice.tip");
  const discountAmount = form.watch("invoice.discount");

  const finalTotal = useMemo(() => {
    return (
      lineItemsTotal +
      (parseFloat(tipAmount?.toString() || "0") || 0) +
      (parseFloat(discountAmount?.toString() || "0") || 0)
    );
  }, [lineItemsTotal, tipAmount, discountAmount]);

  const completedPaymentsTotal = useMemo(() => {
    const total =
      move?.charges
        .filter((charge) => charge.completed)
        .reduce(
          (acc, payment) =>
            acc +
            (Number(payment.amount || 0) -
              Number(payment.processing_fee_amount || 0)),
          0
        ) || 0;

    return Number(total.toFixed(2));
  }, [move?.charges]);

  const currentPaymentsTotal = useMemo(
    () =>
      parseFloat(form.watch("invoice.card_amount")?.toString() || "0") +
      parseFloat(form.watch("invoice.cash_amount")?.toString() || "0") +
      parseFloat(form.watch("invoice.check_amount")?.toString() || "0"),
    [
      form,
      form.watch("invoice.card_amount"),
      form.watch("invoice.cash_amount"),
      form.watch("invoice.check_amount"),
    ]
  );

  const balanceDue = useMemo(
    () => finalTotal - currentPaymentsTotal - completedPaymentsTotal,
    [finalTotal, currentPaymentsTotal, completedPaymentsTotal]
  );

  const handleSubmit = async (data: MoveInputs) => {
    const payload = {
      line_items_attributes: data.line_items.map((item) => ({
        ...item,
      })),
      invoice_attributes: {
        ...data.invoice,
        discount: parseFloat(data.invoice?.discount?.toString() || "0"),
        tip: parseFloat(data.invoice?.tip?.toString() || "0"),
      },
    };

    try {
      await updateMoveApi({
        id: moveId,
        move: payload,
      }).unwrap();

      setIsAlertOpen(false);
      onSuccess?.();
    } catch (error) {
      console.error(error);
    }
  };

  if (isLoading) return <Loading />;
  if (error) return <div>Error loading move data</div>;
  if (!move) return <div>Move not found</div>;

  return (
    <div className={cn("w-full", inApp && "mb-64")}>
      <Form {...form}>
        <form onSubmit={form.handleSubmit(handleSubmit)}>
          <div className="w-full">
            <div className="mt-2">
              <LineItems mode="invoice" buttonsPosition={buttonsPosition} />

              <Separator className="my-4 bg-stone-300 h-[1px]" />

              <div className="space-y-3">
                <div className="grid grid-cols-12 gap-6 flex items-center">
                  <Label className="text-base font-semibold col-span-9 text-right">
                    Subtotal
                  </Label>
                  <div className="text-right text-base font-semibold col-span-3">
                    {Intl.NumberFormat("en-US", {
                      style: "currency",
                      currency: "USD",
                    }).format(lineItemsTotal)}
                  </div>
                </div>

                <div className="grid grid-cols-12 gap-6 flex items-center">
                  <Label className="text-sm font-normal col-span-9 text-right">
                    Final Discount
                  </Label>
                  <div className="flex justify-end items-center col-span-3">
                    <FormField
                      control={form.control}
                      name="invoice.discount"
                      render={({ field }) => (
                        <FormItem>
                          <FormControl>
                            <div className="relative">
                              <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-700 text-sm">
                                $
                              </span>
                              <Input
                                className="pl-6 w-24 text-right [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                                type="number"
                                step="0.01"
                                value={Math.abs(field.value || 0) || ""}
                                onChange={(e) => {
                                  const value = e.target.value;
                                  if (value === "" || value === ".") {
                                    field.onChange(0);
                                  } else {
                                    field.onChange(-1 * parseFloat(value));
                                  }
                                }}
                              />
                            </div>
                          </FormControl>
                        </FormItem>
                      )}
                    />
                  </div>
                </div>

                <div className="grid grid-cols-12 gap-6 flex items-center">
                  <Label className="text-sm font-normal col-span-9 text-right">
                    Crew Tip (not in cash)
                  </Label>
                  <div className="flex justify-end items-center col-span-3">
                    <FormField
                      control={form.control}
                      name="invoice.tip"
                      render={({ field }) => (
                        <FormItem>
                          <FormControl>
                            <div className="relative">
                              <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-700 text-sm">
                                $
                              </span>
                              <Input
                                className="pl-6 w-24 text-right [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                                type="number"
                                step="0.01"
                                value={field.value || ""}
                                onChange={(e) => {
                                  if (e.target.value === "")
                                    field.onChange(null);
                                  else field.onChange(e.target.value);
                                }}
                              />
                            </div>
                          </FormControl>
                        </FormItem>
                      )}
                    />
                  </div>
                </div>

                <div className="grid grid-cols-12 gap-6 flex items-center">
                  <Label className="text-base font-semibold col-span-9 text-right">
                    Final Total
                  </Label>
                  <div className="text-right text-base font-semibold col-span-3">
                    {Intl.NumberFormat("en-US", {
                      style: "currency",
                      currency: "USD",
                    }).format(finalTotal)}
                  </div>
                </div>

                <div className="grid grid-cols-12 gap-6 flex items-center">
                  <Label className="text-sm font-normal col-span-9 text-right">
                    Previously Paid
                  </Label>
                  <div className="text-right text-sm font-normal col-span-3">
                    (
                    {Intl.NumberFormat("en-US", {
                      style: "currency",
                      currency: "USD",
                    }).format(completedPaymentsTotal)}
                    )
                  </div>
                </div>
              </div>

              <div className={cn("space-y-3", inApp && "hidden")}>
                {/* <Separator className="my-4 bg-stone-300 h-[1px]" /> */}
                <div className="my-4 grid grid-cols-12 gap-6 flex items-center">
                  <Label className="text-sm font-normal col-span-9 text-right">
                    Add Manual Payment
                  </Label>
                  <div className="col-span-3 flex justify-end">
                    <ToggleGroup
                      type="multiple"
                      value={selectedPaymentMethods}
                      onValueChange={setSelectedPaymentMethods}
                      className="justify-start"
                    >
                      <ToggleGroupItem
                        className="bg-white px-2 py-0 h-7"
                        value="card"
                      >
                        Card
                      </ToggleGroupItem>
                      <ToggleGroupItem
                        className="bg-white px-2 py-0 h-7"
                        value="cash"
                      >
                        Cash
                      </ToggleGroupItem>
                      <ToggleGroupItem
                        className="bg-white px-2 py-0 h-7"
                        value="check"
                      >
                        Check
                      </ToggleGroupItem>
                    </ToggleGroup>
                  </div>
                </div>

                {(selectedPaymentMethods.includes("card") ||
                  (move.invoice?.card_amount || 0) > 0) && (
                  <div className="grid grid-cols-12 gap-6 flex items-center">
                    <Label className="col-span-9 text-sm font-normal text-right">
                      Amount on card
                    </Label>
                    <div className="col-span-3 flex items-center justify-end gap-2">
                      <Button
                        type="button"
                        variant="secondary"
                        size="sm"
                        onClick={() => {
                          form.setValue(
                            "invoice.card_amount",
                            finalTotal - completedPaymentsTotal
                          );
                          form.setValue("invoice.cash_amount", 0);
                          form.setValue("invoice.check_amount", 0);
                        }}
                      >
                        100%
                      </Button>
                      <FormField
                        control={form.control}
                        name="invoice.card_amount"
                        render={({ field }) => (
                          <FormItem>
                            <FormControl>
                              <div className="relative">
                                <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-700 text-sm">
                                  $
                                </span>
                                <Input
                                  className="pl-6 w-24 text-right [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                                  step="0.01"
                                  value={field.value || ""}
                                  onChange={(e) => {
                                    if (e.target.value === "")
                                      field.onChange(null);
                                    else field.onChange(e.target.value);
                                  }}
                                />
                              </div>
                            </FormControl>
                          </FormItem>
                        )}
                      />
                    </div>
                  </div>
                )}

                {(selectedPaymentMethods.includes("cash") ||
                  (move.invoice?.cash_amount || 0) > 0) && (
                  <div className="grid grid-cols-12 gap-6 flex items-center">
                    <Label className="col-span-9 text-sm font-normal text-right">
                      Amount in cash
                    </Label>
                    <div className="col-span-3 flex items-center justify-end gap-2">
                      <Button
                        type="button"
                        variant="secondary"
                        size="sm"
                        onClick={() => {
                          form.setValue(
                            "invoice.cash_amount",
                            finalTotal - completedPaymentsTotal
                          );
                          form.setValue("invoice.card_amount", 0);
                          form.setValue("invoice.check_amount", 0);
                        }}
                      >
                        100%
                      </Button>
                      <FormField
                        control={form.control}
                        name="invoice.cash_amount"
                        render={({ field }) => (
                          <FormItem>
                            <FormControl>
                              <div className="relative">
                                <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-700 text-sm">
                                  $
                                </span>
                                <Input
                                  className="pl-6 w-24 text-right [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                                  step="0.01"
                                  value={field.value || ""}
                                  onChange={(e) => {
                                    if (e.target.value === "")
                                      field.onChange(null);
                                    else field.onChange(e.target.value);
                                  }}
                                />
                              </div>
                            </FormControl>
                          </FormItem>
                        )}
                      />
                    </div>
                  </div>
                )}

                {(selectedPaymentMethods.includes("check") ||
                  (move.invoice?.check_amount || 0) > 0) && (
                  <>
                    <div className="grid grid-cols-12 gap-6 flex items-center">
                      <Label className="col-span-9 text-sm font-normal text-right">
                        Amount by check
                      </Label>
                      <div className="col-span-3 flex items-center justify-end gap-2">
                        <Button
                          type="button"
                          variant="secondary"
                          size="sm"
                          onClick={() => {
                            form.setValue(
                              "invoice.check_amount",
                              finalTotal - completedPaymentsTotal
                            );
                            form.setValue("invoice.card_amount", 0);
                            form.setValue("invoice.cash_amount", 0);
                          }}
                        >
                          100%
                        </Button>
                        <FormField
                          control={form.control}
                          name="invoice.check_amount"
                          render={({ field }) => (
                            <FormItem>
                              <FormControl>
                                <div className="relative">
                                  <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-700 text-sm">
                                    $
                                  </span>
                                  <Input
                                    className="pl-6 w-24 text-right [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                                    type="number"
                                    step="0.01"
                                    value={field.value || ""}
                                    onChange={(e) => {
                                      if (e.target.value === "")
                                        field.onChange(null);
                                      else field.onChange(e.target.value);
                                    }}
                                  />
                                </div>
                              </FormControl>
                            </FormItem>
                          )}
                        />
                      </div>
                    </div>
                    <div className="col-span-12 flex items-center justify-end">
                      <FormField
                        control={form.control}
                        name="invoice.check_number"
                        render={({ field }) => (
                          <FormItem>
                            <FormControl>
                              <div className="relative">
                                <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-700 text-sm">
                                  Check #
                                </span>
                                <Input
                                  type="text"
                                  value={field.value || ""}
                                  className="pl-6 w-32 text-right [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                                  onChange={(e) => {
                                    if (e.target.value === "")
                                      field.onChange(null);
                                    else field.onChange(e.target.value);
                                  }}
                                />
                              </div>
                            </FormControl>
                          </FormItem>
                        )}
                      />
                    </div>
                  </>
                )}
              </div>

              <div className="mt-3">
                {/* <Separator className="my-4 bg-gray-200" /> */}
                <div className="grid grid-cols-12 gap-6 flex items-center">
                  <Label className="col-span-9 text-base font-semibold text-right">
                    Balance Due
                  </Label>
                  <div className="col-span-3 text-right text-base font-semibold">
                    {Intl.NumberFormat("en-US", {
                      style: "currency",
                      currency: "USD",
                    }).format(balanceDue)}
                  </div>
                </div>
              </div>

              <div
                className={cn(
                  "pt-6 sticky -bottom-6 bg-white flex flex-col gap-2",
                  inApp && "hidden"
                )}
              >
                <div className="flex flex-row gap-2">
                  <Button
                    type="button"
                    variant="outline"
                    className="w-full"
                    onClick={() => {
                      if (
                        window.confirm(
                          "Are you sure you want to cancel updating the final totals for this move?"
                        )
                      ) {
                        onCancel?.();
                      }
                    }}
                  >
                    Cancel
                  </Button>

                  <AlertDialog open={isAlertOpen} onOpenChange={setIsAlertOpen}>
                    <AlertDialogTrigger asChild>
                      <Button
                        type="button"
                        variant="default"
                        className="w-full bg-blue-700 text-white hover:bg-indigo-700 hover:text-white"
                      >
                        Submit
                      </Button>
                    </AlertDialogTrigger>
                    <AlertDialogContent>
                      <AlertDialogHeader>
                        <AlertDialogTitle>
                          Submit Final Amounts?
                        </AlertDialogTitle>
                        <AlertDialogDescription>
                          Please confirm that you want to finalize the job.
                        </AlertDialogDescription>
                      </AlertDialogHeader>
                      <AlertDialogFooter>
                        <AlertDialogCancel>
                          Cancel, Don't Submit
                        </AlertDialogCancel>
                        <AlertDialogAction
                          onClick={form.handleSubmit(handleSubmit)}
                          disabled={isUpdating}
                        >
                          {isUpdating ? (
                            <div className="flex items-center gap-2">
                              <Loader2 className="w-4 h-4 animate-spin" />
                              Submitting...
                            </div>
                          ) : (
                            "Yes, Finalize"
                          )}
                        </AlertDialogAction>
                      </AlertDialogFooter>
                    </AlertDialogContent>
                  </AlertDialog>
                </div>

                <div className="text-sm text-yellow-700 font-normal text-center py-2">
                  $0 balance due will automatically trigger a final receipt to
                  the customer.
                </div>
              </div>
            </div>
          </div>
        </form>
      </Form>
    </div>
  );
};

export default MoveInvoice;
